import { useState, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import { Upload, FileText, CheckCircle, AlertTriangle, Loader2, X } from "lucide-react"
import * as api from "../api/apcm-ops.api"
import type { EnrollFromCSVResult } from "../api/apcm-ops.api"

interface EnrollmentUploadProps {
  orgId: string
  onComplete: () => void
}

export function EnrollmentUpload({ orgId, onComplete }: EnrollmentUploadProps) {
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [initiateConsent, setInitiateConsent] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [result, setResult] = useState<EnrollFromCSVResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = (f: File) => {
    if (!f.name.endsWith(".csv")) {
      setError("Please upload a CSV file")
      return
    }
    setFile(f)
    setError(null)
    setResult(null)
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) handleFile(f)
  }, [])

  const handleUpload = async () => {
    if (!file) return
    setShowConfirm(false)
    setIsUploading(true)
    setError(null)
    setResult(null)

    try {
      const res = await api.enrollFromCSV(orgId, file, initiateConsent)
      setResult(res)
      setFile(null)
      onComplete()
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Upload failed")
    } finally {
      setIsUploading(false)
    }
  }

  const clearState = () => {
    setFile(null)
    setResult(null)
    setError(null)
  }

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        className={cn(
          "relative rounded-xl border-2 border-dashed transition-all",
          "flex flex-col items-center justify-center py-8 px-4 text-center",
          isDragging
            ? "border-primary bg-primary/5"
            : file
              ? "border-primary/40 bg-primary/[0.02]"
              : "border-border/60 hover:border-primary/30 hover:bg-muted/30",
          isUploading && "pointer-events-none opacity-60",
        )}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !file && inputRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) handleFile(f)
            e.target.value = ""
          }}
        />

        {file ? (
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">{file.name}</p>
              <p className="text-[11px] text-muted-foreground">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={(e) => { e.stopPropagation(); clearState() }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <>
            <div className="rounded-full bg-muted/60 p-3 mb-3">
              <Upload className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">
              Drop a CSV file here or click to browse
            </p>
            <p className="text-[11px] text-muted-foreground mt-1 max-w-sm">
              CSV format: external_id, first_name, last_name, date_of_birth, gender, fhir_id, phone_number(s).
              An optional <code className="font-mono">middle_name</code> column may be included anywhere (matched by header).
            </p>
          </>
        )}
      </div>

      {/* Options & submit */}
      {file && !result && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={initiateConsent}
              onCheckedChange={(v) => setInitiateConsent(!!v)}
            />
            <span className="text-xs text-muted-foreground">
              Immediately start consent calls after enrollment
            </span>
          </label>

          <Button
            size="sm"
            className="bg-primary hover:bg-primary-hover text-white text-xs h-9 px-4"
            disabled={isUploading}
            onClick={() => setShowConfirm(true)}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Enrolling...
              </>
            ) : (
              <>
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                Enroll Patients
              </>
            )}
          </Button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="rounded-xl border border-success/30 bg-success/10 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-success" />
            <span className="text-sm font-semibold text-success">Enrollment Complete</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
            <ResultStat label="CSV Rows" value={result.total_csv_rows} />
            <ResultStat label="Created" value={result.patients_created} accent />
            <ResultStat label="Already Existed" value={result.patients_skipped} />
            <ResultStat label="Enrolled" value={result.patients_enrolled} accent />
          </div>
          {result.consent_initiated > 0 && (
            <p className="text-xs text-success mt-1">
              {result.consent_initiated} consent workflows initiated
            </p>
          )}
          {result.enrollment_errors.length > 0 && (
            <div className="flex items-start gap-2 mt-2 rounded-lg bg-warning/10 border border-warning/30 p-2">
              <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" />
              <div>
                <p className="text-[11px] font-medium text-warning">
                  {result.enrollment_errors.length} enrollment error(s)
                </p>
                {result.enrollment_errors.slice(0, 3).map((e, i) => (
                  <p key={i} className="text-[11px] text-warning mt-0.5 font-mono">
                    {e.patient_id.slice(0, 8)}... — {e.error}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-3">
          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Enrollment</DialogTitle>
            <DialogDescription>
              This will create patient records from <strong>{file?.name}</strong> and
              enroll them into the APCM program with state ENROLLMENT_CONFIRMED.
              {initiateConsent && (
                <> Consent call workflows will also be initiated.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary hover:bg-primary-hover"
              onClick={handleUpload}
            >
              Confirm & Enroll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ResultStat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="text-center">
      <p className={cn(
        "text-xl font-semibold tabular-nums",
        accent ? "text-primary" : "text-foreground"
      )}>
        {value}
      </p>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
    </div>
  )
}
